// API Service - central handler for the Get It Media Meeting Scheduler

const API_BASE = '/api';

// Generic fetch wrapper with error handling
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;

  const defaultOptions = {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include' // include cookies for session auth
  };

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: { ...defaultOptions.headers, ...options.headers }
  };

  try {
    const response = await fetch(url, finalOptions);
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || `HTTP ${response.status}`, status: response.status };
    }
    return { success: true, ...data };
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    return { success: false, error: error.message || 'Network error' };
  }
}

function qs(params = {}) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : '';
}

// ============================================
// AUTH
// ============================================
export const authService = {
  login: (email, password) => fetchAPI('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => fetchAPI('/auth/logout', { method: 'POST' }),
  me: () => fetchAPI('/auth/me')
};

// ============================================
// CLIENTS (the customers table, surfaced as Clients)
// ============================================
export const clientsService = {
  getAll: (params = {}) => fetchAPI(`/clients${qs(params)}`),
  getById: (id) => fetchAPI(`/clients/${id}`),
  create: (client) => fetchAPI('/clients', { method: 'POST', body: JSON.stringify(client) }),
  update: (id, client) => fetchAPI(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(client) }),
  delete: (id) => fetchAPI(`/clients/${id}`, { method: 'DELETE' }),
  getAppointments: (id) => fetchAPI(`/clients/${id}/appointments`)
};

// ============================================
// STAFF (the team — bookable people)
// ============================================
export const staffService = {
  getAll: () => fetchAPI('/staff'),
  getById: (id) => fetchAPI(`/staff/${id}`),
  me: () => fetchAPI('/staff/me'),
  updateMe: (data) => fetchAPI('/staff/me', { method: 'PUT', body: JSON.stringify(data) }),
  create: (person) => fetchAPI('/staff', { method: 'POST', body: JSON.stringify(person) }),
  update: (id, person) => fetchAPI(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(person) }),
  deactivate: (id) => fetchAPI(`/staff/${id}`, { method: 'DELETE' })
};

// ============================================
// SERVICES (per-person meeting types)
// ============================================
export const servicesService = {
  getAll: (params = {}) => fetchAPI(`/services${qs(params)}`),
  getById: (id) => fetchAPI(`/services/${id}`),
  create: (service) => fetchAPI('/services', { method: 'POST', body: JSON.stringify(service) }),
  update: (id, service) => fetchAPI(`/services/${id}`, { method: 'PUT', body: JSON.stringify(service) }),
  delete: (id) => fetchAPI(`/services/${id}`, { method: 'DELETE' })
};

// ============================================
// AVAILABILITY (working hours + exceptions + slot preview)
// ============================================
export const availabilityService = {
  getWorkingHours: (params = {}) => fetchAPI(`/availability/working-hours${qs(params)}`),
  updateWorkingHours: (working_hours) => fetchAPI('/availability/working-hours', { method: 'PUT', body: JSON.stringify({ working_hours }) }),
  getExceptions: (params = {}) => fetchAPI(`/availability/exceptions${qs(params)}`),
  addException: (exception) => fetchAPI('/availability/exceptions', { method: 'POST', body: JSON.stringify(exception) }),
  deleteException: (id) => fetchAPI(`/availability/exceptions/${id}`, { method: 'DELETE' }),
  getSlots: (params = {}) => fetchAPI(`/availability/slots${qs(params)}`)
};

// ============================================
// APPOINTMENTS
// ============================================
export const appointmentsService = {
  getAll: (params = {}) => fetchAPI(`/appointments${qs(params)}`),
  getById: (id) => fetchAPI(`/appointments/${id}`),
  create: (appointment) => fetchAPI('/appointments', { method: 'POST', body: JSON.stringify(appointment) }),
  update: (id, appointment) => fetchAPI(`/appointments/${id}`, { method: 'PUT', body: JSON.stringify(appointment) }),
  delete: (id) => fetchAPI(`/appointments/${id}`, { method: 'DELETE' }),
  updateStatus: (id, status) => fetchAPI(`/appointments/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  reschedule: (id, startsAt) => fetchAPI(`/appointments/${id}/reschedule`, { method: 'POST', body: JSON.stringify({ startsAt }) }),
  getCalendar: (start, end, params = {}) => fetchAPI(`/appointments/calendar${qs({ start, end, ...params })}`),
  getDashboard: (params = {}) => fetchAPI(`/appointments/dashboard${qs(params)}`)
};

// ============================================
// SETTINGS
// ============================================
export const settingsService = {
  get: () => fetchAPI('/settings'),
  update: (settings) => fetchAPI('/settings', { method: 'PUT', body: JSON.stringify(settings) })
};

// ============================================
// PUBLIC BOOKING (unauthenticated /api/public/*)
// ============================================
export const publicBookingService = {
  listStaff: () => fetchAPI('/public/staff'),
  getStaffServices: (slug) => fetchAPI(`/public/staff/${slug}/services`),
  getSlots: (serviceId, from, to) => fetchAPI(`/public/services/${serviceId}/slots${qs({ from, to })}`),
  createBooking: (payload) => fetchAPI('/public/bookings', { method: 'POST', body: JSON.stringify(payload) }),
  getBooking: (token) => fetchAPI(`/public/bookings/${token}`),
  cancelBooking: (token) => fetchAPI(`/public/bookings/${token}/cancel`, { method: 'POST' }),
  rescheduleBooking: (token, startsAt) => fetchAPI(`/public/bookings/${token}/reschedule`, { method: 'POST', body: JSON.stringify({ startsAt }) })
};

export default {
  auth: authService,
  clients: clientsService,
  staff: staffService,
  services: servicesService,
  availability: availabilityService,
  appointments: appointmentsService,
  settings: settingsService,
  publicBooking: publicBookingService
};
